import React, { useEffect, useState } from "react";
import {
  TextInput,
  SimpleForm,
  required,
  maxLength,
  number,
  regex,
  CreateProps,
  SelectInput,
  BooleanInput,
  useDataProvider,
  useNotify,
  FormDataConsumer,
} from "react-admin";
import { useField } from "react-final-form";
import { Grid, makeStyles, TextField, Typography } from "@material-ui/core";
import CustomImageInput from "components/CustomImageInput";
import SanitizedGrid from "components/SanitizedGrid";
import CreateToolbar from "components/CustomCreateToolbar";
import { emailValidation, mobilePhoneValidation } from "utils/validation";
import CustomAddressAutoCompleteInput from "./components/CustomAddressAutoCompleteInput";
import CustomOneMap from "./components/CustomOneMap";
import {
  areaCalculationType,
  ccaDropOffLocationType,
  ccaRateChartType,
  paymentMethodType,
  pricingPolicyList,
} from "./const";
import CustomPdfInput from "components/CustomPdfInput";
import { requestPresignedUrl } from "./tools";
import CreateWrapper from "../../components/CreateWrapper";
import { Alert } from "@material-ui/lab";
import CustomNumberInput from "../../components/CustomNumberInput";

const useStyles = makeStyles({
  alertToggleCcaSplitPayment: {
    marginTop: "-35px",
    marginBottom: "25px",
  },
  alertToggleCcaSplitPaymentList: {
    paddingLeft: "15px",
    marginTop: "10px",
    marginBottom: "0px",
  },
  alertToggleCcaSplitPaymentListItem: {
    marginBottom: "5px",
  },
});

const SchoolCreate = (props: CreateProps) => {
  const [markerPosition, setMarker] = useState({ lat: "", lng: "" });
  const [isCcaSettingEnable, setIsCcaSettingEnable] = useState(false);
  const [isStudentSubsidyEnable, setIsStudentSubsidyEnable] = useState(false);
  const [uploadProgressCcaReadme, setUploadProgressCcaReadme] =
    useState<number>(0);
  const [uploadProgressCcaEvent, setUploadProgressCcaEvent] =
    useState<number>(0);
  const provider = useDataProvider();
  const notify = useNotify();
  const classes = useStyles();

  const breadcrumb = [
    { title: "School", link: "/schools" },
    { title: "Create", link: "/schools/create" },
  ];

  const transform = async (data: any) => {
    if (data.photoUrl) {
      if (data.photoUrl.url) {
        data.photoUrl = (data.photoUrl.url as string).split(",")[1];

        delete data.photoUrlFile;
      }
    } else {
      data.photoUrl = null;
    }

    if (data.ccaReadmePdfFile) {
      try {
        const presignedUrl = await requestPresignedUrl(provider);

        const urlFilePath = await provider.uploadFileToAws(
          presignedUrl.pdfUrl,
          {
            data: data.ccaReadmePdfFile.rawFile,
            callback: (percent: number) => {
              setUploadProgressCcaReadme(percent);
            },
          },
        );

        data.ccaReadmePdfUrl = urlFilePath.data;
        delete data.ccaReadmePdfFile;
      } catch (error: any) {
        data.ccaReadmePdfUrl = null;
        notify(`${error.message ?? error.toString()}`, "error");
      }
    } else {
      data.ccaReadmePdfUrl = null;
    }

    if (data.ccaEventPdfFile) {
      try {
        const presignedUrl = await requestPresignedUrl(provider);

        const urlFilePath = await provider.uploadFileToAws(
          presignedUrl.pdfUrl,
          {
            data: data.ccaEventPdfFile.rawFile,
            callback: (percent: number) => {
              setUploadProgressCcaEvent(percent);
            },
          },
        );

        data.ccaEventPdfUrl = urlFilePath.data;
        delete data.ccaEventPdfFile;
      } catch (error: any) {
        data.ccaEventPdfUrl = null;
        notify(`${error.message ?? error.toString()}`, "error");
      }
    } else {
      data.ccaEventPdfUrl = null;
    }

    if (data.pricingPolicy === "contactDays") {
      data.isFullCalendarDaysCharge = false;
    } else {
      data.isFullCalendarDaysCharge = true;
    }

    if (data.ccaDropOffType === "door_to_door") {
      data.isAllowParentSelectWave = false;
    }

    data.maximumStudentSubsidy = (Number(data.maxStudentSubsidy) * 100).toFixed(
      0,
    );

    if (!data.isStudentSubsidyEnable) {
      data.maximumStudentSubsidy = 0;
      data.subsidyDescriptionPrefix = null;
    }

    data.schoolPrefix = data.schoolPrefix.toUpperCase();

    if (!data.isCcaSettingEnable) {
      data.useDifferentCcaSplitPayment = false;
    }

    delete data.pricingPolicy;

    return data;
  };

  const SchoolPrefixFormater = (data: string) => {
    if (data) {
      return data.toUpperCase();
    }
  };

  const callback = (value: { lat: string; lng: string }) => {
    setMarker({ lat: value.lat, lng: value.lng });
  };

  return (
    <>
      <CreateWrapper
        breadcrumb={breadcrumb}
        headerTitle="New School"
        transform={transform}
        {...props}
      >
        <SimpleForm
          toolbar={<CreateToolbar />}
          defaultValue={{
            subsidyDescriptionPrefix: "Enabling Transport Subsidy",
          }}
        >
          <SanitizedGrid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="h6">School Information</Typography>
            </Grid>

            <Grid item xs={4}>
              <TextInput
                label="School Name"
                source="name"
                validate={required()}
              />
            </Grid>

            <Grid item xs={8}></Grid>

            <Grid item xs={4}>
              <TextInput
                label="Email Address"
                source="email"
                type="email"
                validate={emailValidation}
              />
            </Grid>

            <Grid item xs={4}>
              <TextInput
                label="Contact Number"
                source="phone"
                // regular Expression for TextInput doesnt have white spaces
                validate={mobilePhoneValidation}
              />
            </Grid>

            <Grid item xs={4}></Grid>

            <Grid item xs={5}>
              <CustomImageInput
                label="Profile image"
                source="photoUrl"
                fieldType="image"
                accept="image/*"
                placeholder="Drop a images (*.jpg/jpeg/png) to upload, or click to select it."
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6">School Address</Typography>
            </Grid>

            <Grid item xs={4}>
              <CustomAddressAutoCompleteInput
                label="Postal Code *"
                source="postalCode"
                referenceSource="locationData"
                parentCallback={callback}
              />
            </Grid>
            <Grid item xs={8}></Grid>

            <Grid item xs={2}>
              <TextInput label="Unit No." source="unitNumber" />
            </Grid>
            <Grid item xs={2}>
              <TextInput
                label="Block No."
                source="blockNumber"
                validate={required()}
              />
            </Grid>
            <Grid item xs={8}></Grid>

            <Grid item xs={4}>
              <TextInput label="Building Name" source="building" />
            </Grid>
            <Grid item xs={8}></Grid>

            <Grid item xs={4}>
              <TextInput
                label="Address"
                source="address"
                validate={required()}
              />
            </Grid>
            <Grid item xs={8}></Grid>

            {markerPosition.lat !== "" && markerPosition.lng !== "" && (
              <>
                <Grid item xs={12}>
                  <Typography variant="h6">
                    Map Location (Location for school's dropoff/pickup)
                  </Typography>
                </Grid>

                <Grid item xs={6}>
                  <CustomOneMap referenceSource="locationData" />
                  <LatLngInput
                    lat={markerPosition.lat}
                    lng={markerPosition.lng}
                  />
                </Grid>
              </>
            )}

            <Grid item xs={12}>
              <Typography variant="h6">Other Details</Typography>
            </Grid>

            <Grid item xs={3}>
              <TextInput
                label="School Prefix"
                source="schoolPrefix"
                format={SchoolPrefixFormater}
                validate={[required(), regex(/^[A-Za-z0-9]+$/), maxLength(5)]}
                placeholder="Enter Up to 5 Alphanumeric Characters"
              />
            </Grid>

            <Grid item xs={3}>
              <TextInput
                label="Pre VA and Beacon"
                source="virtualAccountPrefix"
                validate={[required(), number(), maxLength(2)]}
                placeholder="Enter Up to 2 Digits"
              />
            </Grid>
            <Grid item xs={6}></Grid>

            <Grid item xs={3}>
              <TextInput
                label="MBC Code"
                source="mbcCode"
                validate={[required()]}
              />
            </Grid>
            <Grid item xs={9}></Grid>

            <Grid item xs={3}>
              <SelectInput
                source="areaDistanceCalculationType"
                label="Type for Student Area Distance Calculation"
                choices={areaCalculationType}
                validate={required()}
              />
            </Grid>
            <Grid item xs={3}>
              <SelectInput
                source="subcontractorAreaDistanceCalculationType"
                label="Type for Subcontractor Area Distance Calculation"
                choices={areaCalculationType}
                validate={required()}
              />
            </Grid>
            <Grid item xs={6}></Grid>

            <Grid item xs={3}>
              <SelectInput
                source="pricingPolicy"
                label="Pricing Policy"
                choices={pricingPolicyList}
                validate={required()}
              />
            </Grid>

            <Grid item xs={3}>
              <SelectInput
                source="paymentMethodType"
                label="Type for Parent Payment Method"
                choices={paymentMethodType}
                validate={required()}
              />
            </Grid>
            <Grid item xs={6}></Grid>

            <Grid item xs={12}>
              <Typography variant="h6">Student Subsidy Settings</Typography>
            </Grid>

            <Grid item xs={4}>
              <BooleanInput
                source="isStudentSubsidyEnable"
                label="Enable Student Subsidy"
                defaultValue={false}
                onChange={(e: boolean) => {
                  setIsStudentSubsidyEnable(e);
                }}
              />
            </Grid>
            <Grid item xs={8}></Grid>

            {isStudentSubsidyEnable && (
              <>
                <Grid item xs={3} style={{ marginTop: "-35px" }}>
                  <CustomNumberInput
                    label="Max Bus Fare Cap ($)"
                    source="maxStudentSubsidy"
                    isRequired
                    isPositiveValue
                  />
                </Grid>
                <Grid item xs={9}></Grid>

                <Grid item xs={6}>
                  <TextInput
                    multiline={true}
                    label="Subsidy Description Format"
                    source="subsidyDescriptionPrefix"
                    inputProps={{ maxLength: 35 }}
                    placeholder="Description is only up to 35 characters"
                    validate={[required(), maxLength(35)]}
                  />
                </Grid>
                <Grid item xs={6}></Grid>

                <FormDataConsumer>
                  {({ formData }) => (
                    <>
                      <Grid item xs={6}>
                        <Alert
                          severity="info"
                          style={{ marginTop: "-15px", marginBottom: "25px" }}
                        >
                          <Typography variant="body2">
                            Description can be up to <strong>35 characters</strong> only.
                            <br />
                            <strong>Format:</strong> [Subsidy Description Format] [Rate Chart Name] - [School MBC Code] - [Student Name]
                            <br />
                            <strong>Example:</strong>{" "}
                            {`${formData.subsidyDescriptionPrefix || "Enabling Transport Subsidy"} November 2025 - ${(formData.mbcCode || "WTS").toUpperCase()} - John Doe`}
                          </Typography>
                        </Alert>
                      </Grid>
                      <Grid item xs={6}></Grid>
                    </>
                  )}
                </FormDataConsumer>
              </>
            )}

            <Grid item xs={4}>
              <BooleanInput
                source="isCustomPickupAndDropoffLocationAllowedForRouteplan"
                label="Allow Custom Pickup and Dropoff Location In Routeplan"
                defaultValue={false}
              />
              <Alert severity="info">
                <Typography variant="body2">
                  If checked, custom pickup and dropoff location (Change
                  Location and Move Marker) will be allowed in routeplan.
                </Typography>
              </Alert>
            </Grid>
            <Grid item xs={8}></Grid>

            <Grid item xs={4}>
              <BooleanInput
                source="isCcaSettingEnable"
                label="Enable CCA Setting"
                defaultValue={false}
                onChange={(e: boolean) => {
                  setIsCcaSettingEnable(e);
                }}
              />
            </Grid>

            {isCcaSettingEnable && (
              <>
                <Grid item xs={12}>
                  <Typography variant="h6">CCA Settings</Typography>
                </Grid>

                <Grid item xs={3}>
                  <SelectInput
                    source="ccaDropOffType"
                    label="Type for CCA Drop-Off Location"
                    choices={ccaDropOffLocationType}
                    validate={required()}
                  />
                </Grid>
                <Grid item xs={3}>
                  <SelectInput
                    source="ccaRateChartType"
                    label="Type for CCA Rate Chart Calculation"
                    choices={ccaRateChartType}
                    validate={required()}
                  />
                </Grid>
                <Grid item xs={6}></Grid>

                <FormDataConsumer>
                  {({ formData }) => {
                    if (
                      formData.ccaDropOffType === "fixed_location" ||
                      formData.ccaDropOffType === "both"
                    ) {
                      return (
                        <>
                          <Grid item xs={12}>
                            <BooleanInput
                              source="isAllowParentSelectWave"
                              label="Allow Parent to Select Wave for CCA Fixed Location Drop-Off Type"
                              defaultValue={false}
                            />
                          </Grid>
                        </>
                      );
                    } else {
                      return <></>;
                    }
                  }}
                </FormDataConsumer>

                <Grid item xs={4}>
                  <BooleanInput
                    source="useDifferentCcaSplitPayment"
                    label="Use Different CCA Split Payment"
                  />
                </Grid>
                <Grid item xs={8}></Grid>

                <Grid item xs={6}>
                  <Alert
                    severity="info"
                    className={classes.alertToggleCcaSplitPayment}
                  >
                    <Typography variant="body2">
                      This toggle enables or disables the{" "}
                      <strong>CCA Split Payment</strong> setting in the{" "}
                      <strong>Payment Setting Menu</strong> on the{" "}
                      <strong>Student Page</strong>.
                    </Typography>
                    <ul className={classes.alertToggleCcaSplitPaymentList}>
                      <li
                        className={classes.alertToggleCcaSplitPaymentListItem}
                      >
                        <strong>Toggle OFF</strong>: The{" "}
                        <strong>CCA Split Payment</strong> setting remains
                        hidden, and the CCA split payment percentages are same
                        as Regular split payment percentages.
                      </li>
                      <li
                        className={classes.alertToggleCcaSplitPaymentListItem}
                      >
                        <strong>Toggle ON</strong>: The{" "}
                        <strong>CCA Split Payment</strong> setting appears in
                        the Payment Setting Menu, allowing admin to configure
                        different CCA split payment percentage for each student.
                      </li>
                    </ul>
                  </Alert>
                </Grid>
                <Grid item xs={6}></Grid>

                <Grid item xs={6}>
                  <BooleanInput
                    source="isAllowManyCcaAyInOneRegularAy"
                    label="Allow Multiple CCA Academic Year In One Regular Academic Year"
                    defaultValue={false}
                  />
                </Grid>
                <Grid item xs={6}></Grid>

                <Grid item xs={6}>
                  <BooleanInput
                    source="isAllowParentUpdateScheduleWhenRenewal"
                    label="Allow Parent Update CCA Schedule When Confirm Renewal"
                    defaultValue={false}
                  />
                </Grid>
                <Grid item xs={6}></Grid>

                <Grid item xs={7}>
                  <CustomPdfInput
                    label="CCA Readme (PDF)"
                    source="ccaReadmePdfFile"
                    urlSource="ccaReadmePdfUrl"
                    uploadProgress={uploadProgressCcaReadme}
                    maxSize={1000000}
                    isRequired={true}
                  />
                </Grid>
                <Grid item xs={5}></Grid>

                <Grid item xs={7}>
                  <CustomPdfInput
                    label="CCA Event (PDF)"
                    source="ccaEventPdfFile"
                    urlSource="ccaEventPdfUrl"
                    uploadProgress={uploadProgressCcaEvent}
                    maxSize={1000000}
                  />
                </Grid>
                <Grid item xs={5}></Grid>
              </>
            )}
          </SanitizedGrid>
        </SimpleForm>
      </CreateWrapper>
    </>
  );
};

const BoundedTextField = ({ name, label, val, onSet }: any) => {
  const {
    input: { onChange, value },
  } = useField(name);

  useEffect(() => {
    onSet && onSet({ type: name, coordinate: value });
  }, []);

  useEffect(() => {
    onChange(val);
  }, [val, onChange]);

  return (
    <TextField name={name} label={label} onChange={onChange} value={val} />
  );
};

export const LatLngInput = ({ lat, lng, updateLocation }: any) => {
  const latLngStyle = {
    display: "flex",
    justifyContent: "space-between",
    marginTop: "10px",
  };

  return (
    <>
      <Grid item xs={6} style={latLngStyle}>
        <BoundedTextField
          name="lat"
          label="Latitude *"
          val={lat}
          onSet={updateLocation}
        />
        <BoundedTextField
          name="lng"
          label="Longitude *"
          val={lng}
          onSet={updateLocation}
        />
      </Grid>
    </>
  );
};

export default SchoolCreate;
